import { Routes, Route, Navigate } from 'react-router';
import { ProtectedRoute } from '@/lib/auth';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { AdminLayout } from '@/layouts/admin-layout';
import { AuthLayout } from '@/layouts/auth-layout';

import LoginPage from '@/pages/auth/login-page';
import CallbackPage from '@/pages/auth/callback-page';
import DashboardPage from '@/pages/dashboard-page';
import SettingsPage from '@/pages/settings-page';
import WorkersListPage from '@/pages/workers/workers-list-page';

import ApplicationsListPage from '@/pages/applications/applications-list-page';
import ApplicationDetailPage from '@/pages/applications/application-detail-page';
import ApplicationBillingPage from '@/pages/applications/application-billing-page';
import ApplicationVisibilityPage from '@/pages/applications/application-visibility-page';
import ApplicationOAuthClientsPage from '@/pages/applications/application-oauth-clients-page';
import CapabilitiesListPage from '@/pages/capabilities/capabilities-list-page';
import CapabilityDetailPage from '@/pages/capabilities/capability-detail-page';

import RegistriesListPage from '@/pages/registries/registries-list-page';
import RegistryDetailPage from '@/pages/registries/registry-detail-page';
import FederationStatusPage from '@/pages/registries/federation-status-page';
import FederationPeersPage from '@/pages/federation/FederationPeersPage';
import OAuthClientsPage from '@/pages/settings/OAuthClientsPage';
import WorkersPage from '@/pages/settings/WorkersPage';
import BillingPage from '@/pages/settings/BillingPage';
import SubscriptionsPage from '@/pages/settings/SubscriptionsPage';
import CustomerLedgerPage from '@/pages/settings/CustomerLedgerPage';
import PlatformBillingPage from '@/pages/admin/PlatformBillingPage';

import MarketplaceProductsPage from '@/pages/marketplace/MarketplaceProductsPage';
import MarketplaceProductDetailPage from '@/pages/marketplace/MarketplaceProductDetailPage';
import MarketplaceBrowsePage from '@/pages/marketplace/MarketplaceBrowsePage';
import MarketplaceCheckoutSuccessPage from '@/pages/marketplace/MarketplaceCheckoutSuccessPage';
import MarketplaceCheckoutCancelPage from '@/pages/marketplace/MarketplaceCheckoutCancelPage';

import TriggerListPage from '@/pages/scheduler/trigger-list-page';
import TriggerDetailPage from '@/pages/scheduler/trigger-detail-page';
import IntegrationsGalleryPage from '@/pages/integrations/integrations-gallery-page';
import ConnectionsListPage from '@/pages/connections/connections-list-page';
import ChatPage from '@/pages/chat/chat-page';
import AccountsListPage from '@/pages/accounts/accounts-list-page';
import AccountDetailPage from '@/pages/accounts/account-detail-page';
import PartnersListPage from '@/pages/partners/partners-list-page';

export function AppRoutes() {
    return (
        <Routes>
            {/* Auth routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/callback" element={<CallbackPage />} />
            </Route>

            {/* Protected admin routes */}
            <Route element={<ProtectedRoute />}>
                <Route element={<AdminLayout />}>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/billing" element={<PermissionGate require={['org:billing:read']}><BillingPage /></PermissionGate>} />
                    <Route path="/settings/subscriptions" element={<PermissionGate require={['org:billing:read']}><SubscriptionsPage /></PermissionGate>} />
                    <Route path="/settings/ledger" element={<PermissionGate require={['org:billing:read']}><CustomerLedgerPage /></PermissionGate>} />
                    <Route path="/settings/oauth-clients" element={<OAuthClientsPage />} />
                    <Route path="/settings/workers" element={<WorkersPage />} />
                    <Route path="/settings/federation-peers" element={<FederationPeersPage />} />

                    {/* Platform Admin */}
                    <Route path="/admin/billing" element={<PermissionGate require={['platform:billing:read']}><PlatformBillingPage /></PermissionGate>} />

                    {/* Marketplace management (provider) */}
                    <Route path="/settings/marketplace" element={<PermissionGate requireAny={['org:billing:read', 'app:billing:read']}><MarketplaceProductsPage /></PermissionGate>} />
                    <Route path="/settings/marketplace/:productId" element={<PermissionGate requireAny={['org:billing:read', 'app:billing:read']}><MarketplaceProductDetailPage /></PermissionGate>} />

                    {/* Marketplace browse (subscriber) */}
                    <Route path="/marketplace" element={<MarketplaceBrowsePage />} />
                    <Route path="/marketplace/checkout/success" element={<MarketplaceCheckoutSuccessPage />} />
                    <Route path="/marketplace/checkout/cancel" element={<MarketplaceCheckoutCancelPage />} />

                    {/* Applications */}
                    <Route path="/applications" element={<ApplicationsListPage />} />
                    <Route path="/applications/:appKey" element={<ApplicationDetailPage />} />
                    <Route path="/applications/:appKey/visibility" element={<ApplicationVisibilityPage />} />
                    <Route path="/applications/:appKey/settings/api-access" element={<ApplicationOAuthClientsPage />} />
                    <Route path="/applications/:appKey/billing" element={<PermissionGate require={['app:billing:read']}><ApplicationBillingPage /></PermissionGate>} />
                    <Route path="/applications/:appKey/billing/subscriptions" element={<PermissionGate require={['app:billing:read']}><SubscriptionsPage /></PermissionGate>} />
                    <Route path="/applications/:appKey/billing/ledger" element={<PermissionGate require={['app:billing:read']}><CustomerLedgerPage /></PermissionGate>} />
                    <Route path="/applications/:appKey/billing/products" element={<PermissionGate require={['app:billing:read']}><MarketplaceProductsPage /></PermissionGate>} />

                    {/* App-scoped capabilities list */}
                    <Route
                        path="/applications/:appKey/capabilities"
                        element={<CapabilitiesListPage />}
                    />

                    {/* Capability detail: type-agnostic URL /applications/:appKey/capabilities/:name */}
                    <Route
                        path="/applications/:appKey/capabilities/:name"
                        element={<CapabilityDetailPage />}
                    />

                    {/* Global capabilities (cross-app) */}
                    <Route path="/capabilities" element={<CapabilitiesListPage />} />

                    {/* Workers */}
                    <Route path="/workers" element={<WorkersListPage />} />

                    {/* Scheduler */}
                    <Route path="/scheduler/triggers" element={<TriggerListPage />} />
                    <Route path="/scheduler/triggers/:id" element={<TriggerDetailPage />} />

                    {/* Chat */}
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/chat/:conversationId" element={<ChatPage />} />

                    {/* Accounts (Plan 56) */}
                    <Route path="/accounts" element={<AccountsListPage />} />
                    <Route path="/accounts/:id" element={<AccountDetailPage />} />

                    {/* Partners (Plan 56 — platform admin only) */}
                    <Route path="/partners" element={<PermissionGate require={['platform:partners:read']}><PartnersListPage /></PermissionGate>} />

                    {/* Integrations & Connections */}
                    <Route path="/integrations" element={<IntegrationsGalleryPage />} />
                    <Route path="/integrations/:appKey/:name" element={<CapabilityDetailPage />} />
                    <Route path="/connections" element={<ConnectionsListPage />} />

                    {/* Registries */}
                    <Route path="/registries" element={<RegistriesListPage />} />
                    <Route path="/registries/federation" element={<FederationStatusPage />} />
                    <Route path="/registries/:registryId" element={<RegistryDetailPage />} />
                </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}
